import {
  EntityStatusChip,
  type EntityStatusTone,
} from '../../../components/entity-detail/EntityStatusChip.js';
import { useTranslation } from '@govard/i18n-core/react';
import { CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Popconfirm, Space, Spin, Typography } from 'antd';
import type { ReactElement } from 'react';

import type { CalendarConnectionSummary } from '../api/calendar-integrations.client.js';
import {
  useCalendarIntegrationMutations,
  useCalendarIntegrations,
} from '../hooks/useCalendarIntegrations.js';

const { Text } = Typography;

const STATUS_TAG: Record<
  CalendarConnectionSummary['status'],
  { tone: EntityStatusTone; key: string }
> = {
  active: { tone: 'success', key: 'crm.settings.calendars.status_active' },
  suspended: { tone: 'warning', key: 'crm.settings.calendars.status_suspended' },
  error: { tone: 'error', key: 'crm.settings.calendars.status_error' },
  revoked: { tone: 'default', key: 'crm.settings.calendars.status_revoked' },
};

export function CalendarIntegrationsPanel(): ReactElement {
  const { t, locale } = useTranslation();
  const query = useCalendarIntegrations();
  const mutations = useCalendarIntegrationMutations();

  const title = t('crm.settings.calendars.title');
  if (query.isLoading) {
    return (
      <Card size="small" title={title}>
        <Spin aria-label={t('crm.settings.calendars.loading')} />
      </Card>
    );
  }
  if (query.isError) {
    return (
      <Card size="small" title={title}>
        <Alert type="warning" showIcon title={t('crm.settings.calendars.load_error')} />
      </Card>
    );
  }

  const data = query.data;
  if (data === undefined || !data.available) {
    return (
      <Card size="small" title={title}>
        <Alert type="info" showIcon title={t('crm.settings.calendars.unavailable')} />
      </Card>
    );
  }

  const connect = async (provider: 'google' | 'outlook'): Promise<void> => {
    const started = await mutations.connect.mutateAsync(provider);
    mutations.connect.reset();
    window.location.assign(started.authorizeUrl);
  };

  return (
    <Card size="small" title={title}>
      <div className="flex w-full flex-col gap-4">
        <Text type="secondary">{t('crm.settings.calendars.description')}</Text>

        <Space wrap>
          {data.providers.google ? (
            <Button
              type="primary"
              icon={<LinkOutlined />}
              loading={mutations.connect.isPending}
              onClick={() => void connect('google')}
            >
              {t('crm.settings.calendars.connect_google')}
            </Button>
          ) : null}
          {data.providers.outlook ? (
            <Button
              icon={<LinkOutlined />}
              loading={mutations.connect.isPending}
              onClick={() => void connect('outlook')}
            >
              {t('crm.settings.calendars.connect_outlook')}
            </Button>
          ) : null}
        </Space>

        {data.connections.length === 0 ? (
          <Text type="secondary">{t('crm.settings.calendars.empty')}</Text>
        ) : (
          <div className="flex flex-col gap-4">
            {data.connections.map((connection) => {
              const status = STATUS_TAG[connection.status];
              return (
                <div
                  key={connection.id}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex flex-col">
                    <Text strong>{connection.accountEmail}</Text>
                    <Text type="secondary">
                      {connection.provider === 'google'
                        ? t('crm.settings.calendars.provider_google')
                        : t('crm.settings.calendars.provider_outlook')}
                      {' · '}
                      {t('crm.settings.calendars.last_sync')}:{' '}
                      {connection.lastSyncedAt === null
                        ? t('crm.settings.calendars.never_synced')
                        : new Intl.DateTimeFormat(locale, {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          }).format(new Date(connection.lastSyncedAt))}
                    </Text>
                  </div>
                  <Space>
                    <EntityStatusChip
                      tone={status.tone}
                      icon={connection.status === 'active' ? <CheckCircleOutlined /> : undefined}
                    >
                      {t(status.key)}
                    </EntityStatusChip>
                    <Popconfirm
                      title={t('crm.settings.calendars.disconnect_title')}
                      description={t('crm.settings.calendars.disconnect_description')}
                      okText={t('crm.settings.calendars.disconnect_confirm')}
                      cancelText={t('crm.common.cancel')}
                      onConfirm={() => mutations.disconnect.mutateAsync(connection.id)}
                    >
                      <Button danger size="small" loading={mutations.disconnect.isPending}>
                        {t('crm.settings.calendars.disconnect')}
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {mutations.connect.isError || mutations.disconnect.isError ? (
        <Alert className="mt-3" type="error" showIcon title={t('crm.settings.calendars.error')} />
      ) : null}
    </Card>
  );
}
